import { useEffect } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { get } from 'lodash';
import {
  VyButton,
  VyCallout,
  VyStack,
  VyToggleSection,
  VyTypography,
  PlusMedium,
} from '@vy/ui-design-system';
import { getCurrencySymbol } from '@vy/services';
import { DEFAULT_CURRENCY } from '@vy/types';
import {
  isNotEmptyString,
  isNotNullOrUndefined,
  isNullOrUndefined,
  last,
  percentage,
  vyDate,
  withDefault,
} from '@vy/utils';
import { useChargeContext } from '../../charge-context';
import { useFormErrorNotificationContext } from '../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { CREATE_PLAN_TABS, type CreatePlanTab } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { getContractEndDate } from '../../../create-plan-page-services/get-contract-end-date';
import { ZERO_DURATION_MONTHS } from '../../../create-plan-page-consts/create-plan-default-values';
import { InstallmentRow } from './installments-section-components/InstallmentRow';

export function InstallmentsSection() {
  const { itemIndex: productIndex } = useChargeContext();
  const { control, getValues, setValue } = useFormContext<PlanInputForm, NewPlanForm>();
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();

  const configPath =
    `fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.scheduledInstallments` as const;
  const installmentsPath = `${configPath}.installments` as const;
  const totalAmountPath = `${configPath}.totalAmount` as const;
  const pricePath = `fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.price` as const;

  const config = useWatch({ control, name: configPath });
  const isEnabled = isNotNullOrUndefined(config);
  const productPrice = withDefault(useWatch({ control, name: pricePath }), 0);

  // The breakdown-sum issue is attached to the installments array root by
  // `PlanInputFormSchema`; read it here to flag a mismatch on the total line
  // and surface it in the create-plan error notification.
  const { errors } = useFormState({ control });
  const sumErrorMessage = (get(errors, installmentsPath) as { message?: string } | undefined)?.message;

  useEffect(() => {
    registerErrorField(installmentsPath, CREATE_PLAN_TABS.serviceFees);
  }, [registerErrorField, installmentsPath]);

  const currencySymbol = getCurrencySymbol(DEFAULT_CURRENCY);

  const contractStartDate = vyDate(getValues('startDate')).toISOString();
  const productCustomStartDate = getValues(`fixedProducts.${productIndex}.startDate`);
  const minimumContractStartDate =
    isNotNullOrUndefined(productCustomStartDate) && isNotEmptyString(String(productCustomStartDate))
      ? vyDate(productCustomStartDate).toISOString()
      : contractStartDate;
  const contractDurationOrZeroMonths = withDefault(getValues('billingData.duration'), ZERO_DURATION_MONTHS);
  const contractEndDate = getContractEndDate(contractStartDate, contractDurationOrZeroMonths);

  // Drive the rows off a watch of the array (not `useFieldArray`): the section
  // seeds/clears the whole config via `setValue` on the parent path when the
  // switch toggles, and a watch re-renders on those writes where a field array's
  // cached `fields` would not.
  const installments = withDefault(useWatch({ control, name: installmentsPath }), []);
  const usedDates = installments.flatMap((item) =>
    isNotNullOrUndefined(item.scheduledDateStr) ? [item.scheduledDateStr] : [],
  );
  const spreadAmount = installments.reduce((sum, item) => sum + (item.amount ?? 0), 0);
  const installmentCount = installments.length;

  // Keep the persisted total in sync with the product price while enabled, so the
  // breakdown denominator and payload stay correct if the price changes.
  useEffect(() => {
    if (isEnabled && config?.totalAmount !== productPrice) {
      setValue(totalAmountPath, productPrice);
    }
  }, [isEnabled, productPrice, config?.totalAmount, setValue, totalAmountPath]);

  const handleToggle = (next: boolean) => {
    if (next) {
      setValue(
        configPath,
        { totalAmount: productPrice, installments: [{ scheduledDateStr: minimumContractStartDate, amount: 0 }] },
        { shouldDirty: true },
      );
    } else {
      setValue(configPath, undefined, { shouldDirty: true });
    }
  };

  const handleAddInstallment = () => {
    const lastDate = last(installments)?.scheduledDateStr ?? minimumContractStartDate;
    const nextInstallment = { scheduledDateStr: vyDate(lastDate).add(1, 'month').toISOString(), amount: 0 };
    setValue(installmentsPath, [...installments, nextInstallment], { shouldDirty: true });
  };

  const handleRemoveInstallment = (index: number) => {
    setValue(
      installmentsPath,
      installments.filter((_, itemIndex) => itemIndex !== index),
      { shouldDirty: true },
    );
  };

  return (
    <VyToggleSection
      title="Multiple Installments"
      description="Split this item's total into multiple scheduled payments"
      checked={isEnabled}
      onChange={handleToggle}
      data-vy="plan-charge-drawer-installments-section"
    >
      <VyStack sx={{ gap: 2 }}>
        <VyTypography variant="subtitle2">Installments Breakdown</VyTypography>

        <VyStack sx={{ gap: 1 }}>
          {installments.map((item, index) => (
            <InstallmentRow
              // Installments have no stable id; rows are RHF-controlled by path, so an index key is safe here.
              key={index}
              index={index}
              control={control}
              datePath={`${installmentsPath}.${index}.scheduledDateStr`}
              amountPath={`${installmentsPath}.${index}.amount`}
              percentageOfTotal={percentage(item.amount ?? 0, productPrice)}
              currencySymbol={currencySymbol}
              usedDates={usedDates}
              contractStartDate={minimumContractStartDate}
              contractEndDate={contractEndDate}
              onRemove={installmentCount > 1 ? () => handleRemoveInstallment(index) : undefined}
            />
          ))}
        </VyStack>

        <VyButton
          variant="text"
          startIcon={<PlusMedium width={16} height={16} />}
          onClick={handleAddInstallment}
          sx={{ alignSelf: 'flex-start', color: 'text.primary' }}
          data-vy="plan-charge-drawer-installments-add"
        >
          Add installment
        </VyButton>

        <VyStack sx={{ gap: 0.5, alignItems: 'flex-end' }}>
          <VyTypography
            variant="body2"
            sx={{ color: isNotNullOrUndefined(sumErrorMessage) ? 'error.main' : 'text.primary' }}
          >
            Total amount: {formatAmount(currencySymbol, spreadAmount)} /{' '}
            {formatAmount(currencySymbol, productPrice)}
          </VyTypography>
          {isNotNullOrUndefined(sumErrorMessage) && (
            <VyTypography variant="fieldHelper" sx={{ color: 'error.main' }}>
              {sumErrorMessage}
            </VyTypography>
          )}
        </VyStack>

        <VyCallout>
          You&apos;ll be paying <strong>{formatAmount(currencySymbol, spreadAmount)}</strong> in{' '}
          <strong>{installmentCount}</strong> installment{installmentCount === 1 ? '' : 's'}
        </VyCallout>
      </VyStack>
    </VyToggleSection>
  );
}

function formatAmount(currencySymbol: string, value: number): string {
  if (isNullOrUndefined(value)) {
    return `${currencySymbol}0`;
  }
  return `${currencySymbol}${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}
